import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, normalizeClass as _normalizeClass, createSlots as _createSlots, createBlock as _createBlock, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-1" };
const _hoisted_2 = { class: "flex items-center gap-2" };
const _hoisted_3 = ["id"];
const _hoisted_4 = ["id"];
const _hoisted_5 = { class: "flex flex-col gap-5" };
const _hoisted_6 = {
    key: 0,
    class: "flex flex-col gap-1"
};
const _hoisted_7 = { class: "text-sm font-medium leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_8 = { class: "flex items-center gap-4" };
const _hoisted_9 = { class: "flex flex-col" };
const _hoisted_10 = ["aria-label"];
const _hoisted_11 = { class: "font-bold" };
const _hoisted_12 = ["id"];
const _hoisted_13 = {
    key: 4,
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
import { computed, ref, toRef, unref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { Alert, AutoComplete, Button, Checkbox, Modal, Popover, RadioButton, TextInput, ToggleInput } from '@eudonet/opal';
import { buildAutoCompleteLabels } from '@/shared/composables/autoCompleteLabels';
import { useAddEmailForm } from '@/shared/composables/channels/useAddEmailForm';
import { useAppToast } from '@/shared/composables/useAppToast';
import { resolveApiErrorMessage } from '@/infrastructure/services/ApiErrorService';
import { logger } from '@/infrastructure/services/LoggerService';
import { buildEmailErrorOverrides } from './channelErrorOverrides';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AddEmailModal',
    props: /*@__PURE__*/ _mergeModels({
        hasPrimaryEmail: { type: Boolean },
        submit: {},
        i18nPrefix: {},
        checkAlreadyUsed: { type: Function, default: undefined },
        withContext: { type: Boolean, default: false },
        organisationSearch: { default: undefined },
        ownerName: { default: '' },
        sharedEmailsEnabled: { type: Boolean, default: false },
        sharedEmailSettingPending: { type: Boolean, default: false },
        errorOverrides: { default: () => ({}) },
        existingRelationOrgIds: { default: () => [] }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Reusable "Ajouter un email" modal (OP-5603 / US-06).
         *
         * Owner-agnostic by design: the persistence call (`submit`), the optional
         * "already used" detection (`checkAlreadyUsed`), the context mode (`withContext`),
         * the organisation autocomplete (`organisationSearch`) and the i18n namespace
         * (`i18nPrefix`) are all injected by the owner wrapper
         * (`ContactAddEmailModal`, `OrganisationAddEmailModal`).
         */
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t } = useI18n();
        const tk = (key, params) => params ? t(`${props.i18nPrefix}.${key}`, params) : t(`${props.i18nPrefix}.${key}`);
        const { toastSuccess } = useAppToast();
        const titleId = useId();
        const hintId = useId();
        const duplicateHelperId = useId();
        const { form, emailError, organisationIdError, emailAlreadyUsed, associatedNames, showSharedOptIn, isProfessional, canSubmit, validate, validateEmail, buildPayload, reset, } = useAddEmailForm({
            hasPrimaryEmail: toRef(props, 'hasPrimaryEmail'),
            withContext: toRef(props, 'withContext'),
            i18nPrefix: toRef(props, 'i18nPrefix'),
            checkAlreadyUsed: props.checkAlreadyUsed,
            sharedEmailsEnabled: toRef(props, 'sharedEmailsEnabled'),
        });
        // The soft "déjà associé" helper shows when the address is already used by another record
        // (duplicate detection) and the user has not yet authorised sharing — Figma 17287-49709. A
        // genuine format/length error (`emailError`) takes precedence and is surfaced via the field's
        // own red-border error state instead. Once sharing is authorised (`form.isShared`) the conflict
        // is resolved and the helper disappears, matching the green "shared" design (OP-6087 §01/§03).
        const showDuplicateMessage = computed(() => emailAlreadyUsed.value && !form.isShared && !emailError.value && !props.sharedEmailSettingPending);
        const organisationSuggestions = computed(() => unref(props.organisationSearch?.suggestions) ?? []);
        const organisationsLoading = computed(() => unref(props.organisationSearch?.isLoading) ?? false);
        const searchOrgs = (query) => props.organisationSearch?.search(query);
        const clearOrgSearch = () => props.organisationSearch?.clear();
        const submitting = ref(false);
        const serverError = ref(null);
        const selectedOrganisationName = ref('');
        async function handleSubmit() {
            if (!validate())
                return;
            submitting.value = true;
            serverError.value = null;
            try {
                await props.submit(buildPayload());
                emit('created');
                reset();
                selectedOrganisationName.value = '';
                open.value = false;
                toastSuccess(`${props.i18nPrefix}.saveSuccess`);
            }
            catch (error) {
                serverError.value = resolveApiErrorMessage(error, buildEmailErrorOverrides(props.i18nPrefix, props.errorOverrides), `${props.i18nPrefix}.errors.serverError`);
                logger.error('Failed to add email', error);
            }
            finally {
                submitting.value = false;
            }
        }
        function handleCancel() {
            reset();
            clearOrgSearch();
            selectedOrganisationName.value = '';
            serverError.value = null;
            open.value = false;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": [
                    _cache[7] || (_cache[7] = ($event) => ((open).value = $event)),
                    _cache[8] || (_cache[8] = (v) => !v && handleCancel())
                ],
                size: "sm",
                "show-close-button": true,
                "show-header-border": false,
                "show-footer-border": false,
                "footer-position": "right",
                "aria-labelledby": _unref(titleId),
                "aria-describedby": __props.withContext ? _unref(hintId) : undefined
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("div", _hoisted_2, [
                            _cache[9] || (_cache[9] = _createElementVNode("span", {
                                class: "flex h-9 items-center justify-center rounded-lg border border-(--border-border-base-soft) px-3 dark:border-(--border-border-base)",
                                "aria-hidden": "true"
                            }, [
                                _createElementVNode("i", { class: "ri-mail-line text-xl text-(--text-text-black-default) dark:text-(--text-text-white-default)" })
                            ], -1 /* CACHED */)),
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(tk('title')), 9 /* TEXT, PROPS */, _hoisted_3)
                        ]),
                        (__props.withContext)
                            ? (_openBlock(), _createElementBlock("p", {
                                key: 0,
                                id: _unref(hintId),
                                class: "text-sm font-normal leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(tk('requiredFieldsHint')), 9 /* TEXT, PROPS */, _hoisted_4))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: tk('actions.cancel'),
                        onClick: handleCancel
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: tk('actions.submit'),
                        disabled: !_unref(canSubmit) || submitting.value,
                        loading: submitting.value,
                        onClick: handleSubmit
                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        (__props.withContext)
                            ? (_openBlock(), _createElementBlock("fieldset", _hoisted_6, [
                                _createElementVNode("legend", _hoisted_7, [
                                    _createTextVNode(_toDisplayString(tk('context.label')) + " ", 1 /* TEXT */),
                                    _cache[10] || (_cache[10] = _createElementVNode("span", { "aria-hidden": "true" }, "*", -1 /* CACHED */))
                                ]),
                                _createElementVNode("div", _hoisted_8, [
                                    _createVNode(_unref(RadioButton), {
                                        modelValue: _unref(form).context,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(form).context) = $event)),
                                        value: 'Professional',
                                        name: "add-email-context",
                                        label: tk('context.professional')
                                    }, null, 8 /* PROPS */, ["modelValue", "label"]),
                                    _createVNode(_unref(RadioButton), {
                                        modelValue: _unref(form).context,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((_unref(form).context) = $event)),
                                        value: 'Personal',
                                        name: "add-email-context",
                                        label: tk('context.personal')
                                    }, null, 8 /* PROPS */, ["modelValue", "label"])
                                ])
                            ]))
                            : _createCommentVNode("v-if", true),
                        _createElementVNode("div", _hoisted_9, [
                            _createVNode(_unref(TextInput), {
                                modelValue: _unref(form).email,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((_unref(form).email) = $event)),
                                type: "email",
                                label: tk('email.label'),
                                placeholder: tk('email.placeholder'),
                                error: _unref(emailError),
                                "icon-left": "ri-mail-send-line",
                                "full-width": "",
                                class: _normalizeClass(_unref(emailError) ? '' : '-mb-4.5'),
                                "aria-describedby": showDuplicateMessage.value ? _unref(duplicateHelperId) : undefined,
                                onBlur: _unref(validateEmail)
                            }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                                (_unref(associatedNames).length)
                                    ? {
                                        name: "iconRight",
                                        fn: _withCtx(() => [
                                            _createVNode(_unref(Popover), {
                                                position: "top",
                                                header: tk('duplicate.title')
                                            }, {
                                                trigger: _withCtx(({ triggerProps }) => [
                                                    _createElementVNode("button", _mergeProps(triggerProps, {
                                                        type: "button",
                                                        class: ["flex items-center", _unref(form).isShared ? 'text-(--colors-icon-success)' : 'text-(--colors-icon-warning)'],
                                                        "aria-label": tk('duplicate.title')
                                                    }), [...(_cache[11] || (_cache[11] = [
                                                            _createElementVNode("i", {
                                                                class: "ri-group-2-line text-base",
                                                                "aria-hidden": "true"
                                                            }, null, -1 /* CACHED */)
                                                        ]))], 16 /* FULL_PROPS */, _hoisted_10)
                                                ]),
                                                default: _withCtx(() => [
                                                    _createElementVNode("span", null, _toDisplayString(`${tk('duplicate.associatedWith')} `), 1 /* TEXT */),
                                                    _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(associatedNames).join(', ')), 1 /* TEXT */)
                                                ]),
                                                _: 1 /* STABLE */
                                            }, 8 /* PROPS */, ["header"])
                                        ]),
                                        key: "0"
                                    }
                                    : undefined
                            ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "label", "placeholder", "error", "class", "aria-describedby", "onBlur"]),
                            (showDuplicateMessage.value)
                                ? (_openBlock(), _createElementBlock("p", {
                                    key: 0,
                                    id: _unref(duplicateHelperId),
                                    class: "flex items-center gap-1 px-px text-xs text-(--text-text-error-strong) dark:text-(--text-text-error)",
                                    role: "alert"
                                }, [
                                    _cache[12] || (_cache[12] = _createElementVNode("i", {
                                        class: "ri-error-warning-fill",
                                        "aria-hidden": "true"
                                    }, null, -1 /* CACHED */)),
                                    _createElementVNode("span", null, _toDisplayString(tk('validation.emailAlreadyUsed')), 1 /* TEXT */)
                                ], 8 /* PROPS */, _hoisted_12))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (_unref(showSharedOptIn))
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                class: _normalizeClass([
                                    'flex items-center gap-2.5 p-4 rounded-xl border w-full',
                                    _unref(form).isShared
                                        ? 'bg-(--background-background-success) border-(--border-border-success)'
                                        : 'bg-(--background-background-warning) border-(--border-border-warning-subtle)',
                                ])
                            }, [
                                _createVNode(_unref(Checkbox), {
                                    modelValue: _unref(form).isShared,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((_unref(form).isShared) = $event)),
                                    label: tk('shared'),
                                    variant: "default",
                                    "label-wrap": ""
                                }, null, 8 /* PROPS */, ["modelValue", "label"])
                            ], 2 /* CLASS */))
                            : _createCommentVNode("v-if", true),
                        (_unref(isProfessional))
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                _createElementVNode("div", {
                                    class: _normalizeClass(_unref(organisationIdError) ? '' : '-mb-4.5')
                                }, [
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: _unref(form).organisationId,
                                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((_unref(form).organisationId) = $event)),
                                        label: tk('organisation.label'),
                                        placeholder: tk('organisation.placeholder'),
                                        "empty-label": tk('organisation.empty'),
                                        options: organisationSuggestions.value,
                                        loading: organisationsLoading.value,
                                        error: _unref(organisationIdError),
                                        required: true,
                                        debounce: 250,
                                        "icon-left": "ri-building-line",
                                        "icon-right": "ri-search-line",
                                        "full-width": "",
                                        onSearch: searchOrgs,
                                        onSelect: _cache[5] || (_cache[5] = (opt) => (selectedOrganisationName.value = opt.label)),
                                        onClear: clearOrgSearch
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "empty-label", "options", "loading", "error"])
                                ], 2 /* CLASS */),
                                (_unref(form).organisationId && !__props.existingRelationOrgIds.includes(_unref(form).organisationId))
                                    ? (_openBlock(), _createBlock(_unref(Alert), {
                                        key: 0,
                                        class: "mb-5",
                                        color: "info",
                                        heading: tk('organisation.relationHint', { contact: __props.ownerName, organisation: selectedOrganisationName.value })
                                    }, null, 8 /* PROPS */, ["heading"]))
                                    : _createCommentVNode("v-if", true)
                            ], 64 /* STABLE_FRAGMENT */))
                            : _createCommentVNode("v-if", true),
                        (__props.hasPrimaryEmail)
                            ? (_openBlock(), _createBlock(_unref(ToggleInput), {
                                key: 3,
                                modelValue: _unref(form).isPrimary,
                                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((_unref(form).isPrimary) = $event)),
                                type: "default",
                                label: tk('primary')
                            }, null, 8 /* PROPS */, ["modelValue", "label"]))
                            : _createCommentVNode("v-if", true),
                        (serverError.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                _cache[13] || (_cache[13] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(serverError.value), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby", "aria-describedby"]));
        };
    }
});
